import { supabase } from '@/integrations/supabase/client';
import type { 
  Deadline, 
  DeadlineTemplate, 
  CreateDeadlineInput, 
  UpdateDeadlineInput,
  DeadlineCategory,
  DeadlineStatus,
  HolidayScope
} from '@/types/deadlines';
import { calculateDueDate, getNextFixedDate } from './dateCalculationService';

/**
 * Obtiene todos los plazos del usuario actual
 */
export async function getDeadlines(
  status?: DeadlineStatus | DeadlineStatus[],
  category?: DeadlineCategory
): Promise<Deadline[]> {
  let query = supabase
    .from('deadlines')
    .select('*')
    .order('due_date', { ascending: true });

  if (status) {
    if (Array.isArray(status)) {
      query = query.in('status', status);
    } else {
      query = query.eq('status', status);
    }
  }

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching deadlines:', error);
    throw error;
  }

  return (data || []) as Deadline[];
}

/**
 * Obtiene un plazo por ID
 */
export async function getDeadlineById(id: string): Promise<Deadline | null> {
  const { data, error } = await supabase
    .from('deadlines')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching deadline:', error);
    return null;
  }

  return data as Deadline;
}

/**
 * Crea un nuevo plazo
 */
export async function createDeadline(
  input: CreateDeadlineInput,
  userId: string
): Promise<Deadline | null> {
  const { data, error } = await supabase
    .from('deadlines')
    .insert({
      user_id: userId,
      title: input.title,
      description: input.description || null,
      expediente_number: input.expediente_number || null,
      interested_party: input.interested_party || null,
      due_date: input.due_date,
      original_due_date: input.original_due_date || input.due_date,
      category: input.category || 'otros',
      priority: input.priority || 'media',
      day_type: input.day_type || 'habiles',
      reminder_days: input.reminder_days || [7, 3, 1, 0],
      is_critical: input.is_critical || false,
      legal_reference: input.legal_reference || null,
      template_id: input.template_id || null,
      source_type: input.source_type || 'manual',
      chat_id: input.chat_id || null,
      document_path: input.document_path || null,
      organization_id: input.organization_id || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating deadline:', error);
    throw error;
  }

  return data as Deadline;
}

/**
 * Actualiza un plazo existente
 */
export async function updateDeadline(
  id: string,
  input: UpdateDeadlineInput
): Promise<Deadline | null> {
  const { data, error } = await supabase
    .from('deadlines')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating deadline:', error);
    throw error;
  }

  return data as Deadline;
}

/**
 * Elimina un plazo
 */
export async function deleteDeadline(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('deadlines')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting deadline:', error);
    return false;
  }

  return true;
}

/**
 * Marca un plazo como completado
 */
export async function markDeadlineComplete(id: string): Promise<Deadline | null> {
  return updateDeadline(id, { status: 'completado' });
}

/**
 * Obtiene las plantillas disponibles
 */
export async function getDeadlineTemplates(
  scope?: HolidayScope | HolidayScope[],
  category?: DeadlineCategory
): Promise<DeadlineTemplate[]> {
  let query = supabase
    .from('deadline_templates')
    .select('*')
    .eq('is_active', true)
    .order('category')
    .order('title');

  if (scope) {
    if (Array.isArray(scope)) {
      query = query.in('scope', scope);
    } else {
      query = query.eq('scope', scope);
    }
  }

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching templates:', error);
    return [];
  }

  return (data || []) as DeadlineTemplate[];
}

/**
 * Crea un plazo a partir de una plantilla
 */
export async function createDeadlineFromTemplate(
  template: DeadlineTemplate,
  userId: string,
  autonomousCommunity: string = 'otra',
  organizationId?: string
): Promise<Deadline | null> {
  let dueDate: Date;
  let originalDueDate: Date;

  if (template.day_type === 'fecha_fija' && template.fixed_date) {
    // Calcular la próxima fecha fija
    originalDueDate = getNextFixedDate(template.fixed_date);
    dueDate = originalDueDate;
  } else if (template.default_days) {
    // Calcular plazo desde hoy
    const result = await calculateDueDate(
      new Date(),
      template.default_days,
      template.day_type,
      autonomousCommunity,
      organizationId
    );
    dueDate = result.dueDate;
    originalDueDate = result.originalDueDate;
  } else {
    // Sin plazo definido, usar fecha actual
    dueDate = new Date();
    originalDueDate = dueDate;
  }

  return createDeadline(
    {
      title: template.title,
      description: template.description || undefined,
      due_date: dueDate.toISOString().split('T')[0],
      original_due_date: originalDueDate.toISOString().split('T')[0],
      category: template.category,
      day_type: template.day_type,
      is_critical: template.is_critical,
      legal_reference: template.legal_reference || undefined,
      template_id: template.id,
      source_type: 'template',
      organization_id: organizationId,
    },
    userId
  );
}

/**
 * Obtiene los plazos que vencen pronto
 */
export async function getUpcomingDeadlines(days: number = 7): Promise<Deadline[]> {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + days);

  const { data, error } = await supabase
    .from('deadlines')
    .select('*')
    .in('status', ['pendiente', 'en_curso'])
    .gte('due_date', today.toISOString().split('T')[0])
    .lte('due_date', futureDate.toISOString().split('T')[0])
    .order('due_date', { ascending: true });

  if (error) {
    console.error('Error fetching upcoming deadlines:', error);
    return [];
  }

  return (data || []) as Deadline[];
}

/**
 * Obtiene los plazos vencidos que no están marcados como vencidos
 */
export async function getOverdueDeadlines(): Promise<Deadline[]> {
  const today = new Date().toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('deadlines')
    .select('*')
    .in('status', ['pendiente', 'en_curso'])
    .lt('due_date', today)
    .order('due_date', { ascending: true });

  if (error) {
    console.error('Error fetching overdue deadlines:', error);
    return [];
  }

  return (data || []) as Deadline[];
}

/**
 * Actualiza el estado de plazos vencidos
 */
export async function updateOverdueDeadlines(): Promise<number> {
  const today = new Date().toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('deadlines')
    .update({ status: 'vencido' as DeadlineStatus })
    .in('status', ['pendiente', 'en_curso'])
    .lt('due_date', today)
    .select();

  if (error) {
    console.error('Error updating overdue deadlines:', error);
    return 0;
  }

  return data?.length || 0;
}

/**
 * Cuenta los plazos urgentes (próximos 3 días)
 */
export async function countUrgentDeadlines(): Promise<number> {
  const today = new Date();
  const urgentDate = new Date();
  urgentDate.setDate(today.getDate() + 3);

  const { count, error } = await supabase
    .from('deadlines')
    .select('*', { count: 'exact', head: true })
    .in('status', ['pendiente', 'en_curso'])
    .lte('due_date', urgentDate.toISOString().split('T')[0]);

  if (error) {
    console.error('Error counting urgent deadlines:', error);
    return 0;
  }

  return count || 0;
}
